import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  Icon,
  Flex,
  Image,
  Button,
  useBreakpointValue,
  Text,
  VStack,
  Tooltip,
  useColorMode,
  IconButton,
  Collapse,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
} from '@chakra-ui/react';
import {
  MdDashboard,
  MdSettings,
  MdPerson,
  MdLogout,
  MdAccountCircle,
  MdPhoneIphone,
  MdLibraryBooks,
  MdPayment,
  MdAdminPanelSettings,
  MdVpnKey,
  MdMenu,
  MdDarkMode,
  MdLightMode,
  MdReceipt,
  MdDescription,
} from 'react-icons/md';
import { RiFunctionAddLine } from "react-icons/ri";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { ReactComponent as MCPServerIcon } from '../assets/icons/mcp.svg';

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { colorMode, toggleColorMode } = useColorMode();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isAdminOpen, onToggle: onAdminToggle } = useDisclosure();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const navItems = [
    { label: 'Dashboard', icon: MdDashboard, route: '/dashboard' },
    { label: 'Assistant', icon: MdPerson, route: '/assistant' },
    { label: 'Phone Numbers', icon: MdPhoneIphone, route: '/phone-numbers' },
    { label: 'Knowledgebase', icon: MdLibraryBooks, route: '/knowledgebase' },
    { label: 'Tool', icon: RiFunctionAddLine, route: '/tool' },
    { label: 'MCP', icon: MCPServerIcon, route: '/mcp' },
    { label: 'Settings', icon: MdSettings, route: '/settings' },
    { label: 'API Keys', icon: MdVpnKey, route: '/api-keys' },
    { label: 'API Docs', icon: MdDescription, route: '/api-docs' },
    { label: 'Profile', icon: MdAccountCircle, route: '/profile' },
    { label: 'Usage', icon: MdPayment, route: '/billing' },
    { label: 'Invoices', icon: MdReceipt, route: '/invoices' }
  ];

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        const response = await axios.get('/api/protected/checkIsAdmin');
        setIsAdmin(response.data.isAdmin);
      } catch (error) {
        console.error('Error checking session:', error);
        setIsAdmin(false);
      }
    };
    checkAuthentication();
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) {
      onAdminToggle();
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await axios.post('/api/protected/logout');
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleColorModeChange = async () => {
    toggleColorMode();

    try {
      const preferencesToSend = {
        colorMode
      };
      const response = await axios.put(`/api/protected/preferences`, preferencesToSend);
      return response.data.data;
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  };

  const renderNavItem = (item) => {
    if (item.is_admin && !isAdmin) return null;

    const isActive = location.pathname.startsWith(item.route);

    const NavItemContent = (
      <Flex
        align="center"
        p={2}
        borderRadius="md"
        bg={isActive ? (colorMode === 'light' ? 'gray.200' : 'gray.700') : 'transparent'}
        _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.700' }}
        transition="all 0.2s"
      >
        <Icon as={item.icon} boxSize={{ base: '2rem', md: '1.5rem' }} mr={2} />
        <Text
          display={{ base: 'none', md: isCollapsed ? 'none' : 'block' }}
          fontSize="lg"
        >
          {item.label}
        </Text>
      </Flex>
    );

    return (
      <Link
        key={item.route}
        to={item.route}
        style={{ textDecoration: 'none' }}
      >
        {isMobile || isCollapsed ? (
          <Tooltip label={item.label} placement="right">
            {NavItemContent}
          </Tooltip>
        ) : (
          NavItemContent
        )}
      </Link>
    );
  };

  const MobileNavigation = () => (
    <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>Menu</DrawerHeader>
        <DrawerBody>
          <VStack spacing={4} align="stretch">
            {navItems.map(renderNavItem)}
            {isAdmin && (
              <Box>
                <Flex
                  align="center"
                  p={2}
                  cursor="pointer"
                  onClick={onAdminToggle}
                  borderRadius="md"
                  bg={location.pathname.startsWith('/admin') ? (colorMode === 'light' ? 'gray.200' : 'gray.700') : 'transparent'}
                  _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.700' }}
                >
                  <Icon as={MdAdminPanelSettings} boxSize={{ base: '2rem', md: '1.5rem' }} mr={2} />
                  <Text
                    display={{ base: 'none', md: isCollapsed ? 'none' : 'block' }}
                    fontSize="lg"
                  >
                    Admin
                  </Text>
                  {!isCollapsed && (
                    <Icon
                      as={isAdminOpen ? ChevronUpIcon : ChevronDownIcon}
                      ml="auto"
                      display={{ base: 'none', md: 'block' }}
                    />
                  )}
                </Flex>
                <Collapse in={isAdminOpen || location.pathname.startsWith('/admin')} animateOpacity>
                  <Box position="relative" pl={6} mt={2}>
                    <Box
                      position="absolute"
                      left="1.25rem"
                      top="0"
                      bottom="0"
                      w="2px"
                      bg={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                    />
                    <VStack align="stretch" spacing={2}>
                      <Link to="/admin/create-user" style={{ textDecoration: 'none' }}>
                        <Text
                          p={2}
                          borderRadius="md"
                          cursor="pointer"
                          bg={location.pathname === '/admin/create-user' ? (colorMode === 'light' ? 'gray.200' : 'gray.700') : 'transparent'}
                          _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.700' }}
                        >
                          Create User
                        </Text>
                      </Link>
                      <Link to="/admin/users" style={{ textDecoration: 'none' }}>
                        <Text
                          p={2}
                          borderRadius="md"
                          cursor="pointer"
                          bg={location.pathname === '/admin/users' ? (colorMode === 'light' ? 'gray.200' : 'gray.700') : 'transparent'}
                          _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.700' }}
                        >
                          All Users
                        </Text>
                      </Link>
                      <Link to="/admin/collections" style={{ textDecoration: 'none' }}>
                        <Text
                          p={2}
                          borderRadius="md"
                          cursor="pointer"
                          bg={location.pathname === '/admin/collections' ? (colorMode === 'light' ? 'gray.200' : 'gray.700') : 'transparent'}
                          _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.700' }}
                        >
                          List Collections
                        </Text>
                      </Link>
                      <Link to="/admin/models" style={{ textDecoration: 'none' }}>
                        <Text
                          p={2}
                          borderRadius="md"
                          cursor="pointer"
                          bg={location.pathname === '/admin/models' ? (colorMode === 'light' ? 'gray.200' : 'gray.700') : 'transparent'}
                          _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.700' }}
                        >
                          Models
                        </Text>
                      </Link>
                      <Link to="/admin/unbilled" style={{ textDecoration: 'none' }}>
                        <Text
                          p={2}
                          borderRadius="md"
                          cursor="pointer"
                          bg={location.pathname === '/admin/unbilled' ? (colorMode === 'light' ? 'gray.200' : 'gray.700') : 'transparent'}
                          _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.700' }}
                        >
                          Unbilled
                        </Text>
                      </Link>
                      <Link to="/admin/invoices" style={{ textDecoration: 'none' }}>
                        <Text
                          p={2}
                          borderRadius="md"
                          cursor="pointer"
                          bg={location.pathname === '/admin/invoices' ? (colorMode === 'light' ? 'gray.200' : 'gray.700') : 'transparent'}
                          _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.700' }}
                        >
                          Invoices
                        </Text>
                      </Link>
                    </VStack>
                  </Box>
                </Collapse>
              </Box>
            )}
            <Button
              onClick={handleLogout}
              leftIcon={<MdLogout />}
              colorScheme="red"
              variant="ghost"
            >
              Logout
            </Button>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );

  const DesktopNavigation = () => (
    <Flex
      direction="column"
      alignItems="center"
      justifyContent="space-between"
      bg={colorMode === 'light' ? 'white' : 'gray.800'}
      color={colorMode === 'light' ? 'gray.800' : 'white'}
      p={4}
      minH="100vh"
      h="100%"
      w={{ base: 'full', md: isCollapsed ? '80px' : '250px' }}
      boxShadow="sm"
      position="sticky"
      top={0}
      left={0}
    >
      <Box>
        <Flex alignItems="center" mb={6} justifyContent="center">
          <Image
            src={colorMode === 'light' ? "/logo.png" : "/logo-dark.png"}
            alt="Logo"
            width={isCollapsed ? "30px" : "200px"}
            display={{ base: 'none', md: 'block' }}
          />
        </Flex>

        <VStack as="nav" spacing={4} align="stretch">
          {navItems.map(renderNavItem)}
          {isAdmin && (
            <Box>
              <Flex
                align="center"
                p={2}
                cursor="pointer"
                onClick={onAdminToggle}
                borderRadius="md"
                bg={location.pathname.startsWith('/admin') ? (colorMode === 'light' ? 'gray.200' : 'gray.700') : 'transparent'}
                _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.700' }}
              >
                <Icon as={MdAdminPanelSettings} boxSize={{ base: '2rem', md: '1.5rem' }} mr={2} />
                <Text
                  display={{ base: 'none', md: isCollapsed ? 'none' : 'block' }}
                  fontSize="lg"
                >
                  Admin
                </Text>
                {!isCollapsed && (
                  <Icon
                    as={isAdminOpen ? ChevronUpIcon : ChevronDownIcon}
                    ml="auto"
                    display={{ base: 'none', md: 'block' }}
                  />
                )}
              </Flex>
              <Collapse in={isAdminOpen || location.pathname.startsWith('/admin')} animateOpacity>
                <Box position="relative" pl={6} mt={2}>
                  <Box
                    position="absolute"
                    left="1.25rem"
                    top="0"
                    bottom="0"
                    w="2px"
                    bg={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                  />
                  <VStack align="stretch" spacing={2}>
                    <Link to="/admin/create-user" style={{ textDecoration: 'none' }}>
                      <Text
                        p={2}
                        borderRadius="md"
                        cursor="pointer"
                        bg={location.pathname === '/admin/create-user' ? (colorMode === 'light' ? 'gray.200' : 'gray.700') : 'transparent'}
                        _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.700' }}
                      >
                        Create User
                      </Text>
                    </Link>
                    <Link to="/admin/users" style={{ textDecoration: 'none' }}>
                      <Text
                        p={2}
                        borderRadius="md"
                        cursor="pointer"
                        bg={location.pathname === '/admin/users' ? (colorMode === 'light' ? 'gray.200' : 'gray.700') : 'transparent'}
                        _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.700' }}
                      >
                        All Users
                      </Text>
                    </Link>
                    <Link to="/admin/collections" style={{ textDecoration: 'none' }}>
                      <Text
                        p={2}
                        borderRadius="md"
                        cursor="pointer"
                        bg={location.pathname === '/admin/collections' ? (colorMode === 'light' ? 'gray.200' : 'gray.700') : 'transparent'}
                        _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.700' }}
                      >
                        List Collections
                      </Text>
                    </Link>
                    <Link to="/admin/models" style={{ textDecoration: 'none' }}>
                      <Text
                        p={2}
                        borderRadius="md"
                        cursor="pointer"
                        bg={location.pathname === '/admin/models' ? (colorMode === 'light' ? 'gray.200' : 'gray.700') : 'transparent'}
                        _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.700' }}
                      >
                        Models
                      </Text>
                    </Link>
                    <Link to="/admin/unbilled" style={{ textDecoration: 'none' }}>
                      <Text
                        p={2}
                        borderRadius="md"
                        cursor="pointer"
                        bg={location.pathname === '/admin/unbilled' ? (colorMode === 'light' ? 'gray.200' : 'gray.700') : 'transparent'}
                        _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.700' }}
                      >
                        Unbilled
                      </Text>
                    </Link>
                    <Link to="/admin/invoices" style={{ textDecoration: 'none' }}>
                      <Text
                        p={2}
                        borderRadius="md"
                        cursor="pointer"
                        bg={location.pathname === '/admin/invoices' ? (colorMode === 'light' ? 'gray.200' : 'gray.700') : 'transparent'}
                        _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.700' }}
                      >
                        Invoices
                      </Text>
                    </Link>
                  </VStack>
                </Box>
              </Collapse>
            </Box>
          )}
        </VStack>
      </Box>

      <Box width="full">
        <Box
          height="1px"
          bg={colorMode === 'light' ? 'gray.200' : 'gray.700'}
          my={4}
          width="full"
        />

        <VStack spacing={2} width="full">
          <Flex
            align="center"
            p={2}
            borderRadius="md"
            _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.700' }}
            transition="all 0.2s"
            onClick={handleColorModeChange}
            cursor="pointer"
            width="full"
          >
            <Icon as={colorMode === 'light' ? MdDarkMode : MdLightMode} boxSize={{ base: '2rem', md: '1.5rem' }} mr={2} />
            <Text
              display={{ base: 'none', md: isCollapsed ? 'none' : 'block' }}
              fontSize="lg"
            >
              {colorMode === 'light' ? 'Dark Mode' : 'Light Mode'}
            </Text>
          </Flex>

          <Flex
            align="center"
            p={2}
            borderRadius="md"
            _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.700' }}
            transition="all 0.2s"
            onClick={handleLogout}
            cursor="pointer"
            width="full"
            color="red.500"
          >
            <Icon as={MdLogout} boxSize={{ base: '2rem', md: '1.5rem' }} mr={2} />
            <Text
              display={{ base: 'none', md: isCollapsed ? 'none' : 'block' }}
              fontSize="lg"
            >
              Logout
            </Text>
          </Flex>

          <Flex
            align="center"
            p={2}
            borderRadius="md"
            _hover={{ bg: colorMode === 'light' ? 'gray.100' : 'gray.700' }}
            transition="all 0.2s"
            onClick={() => setIsCollapsed(!isCollapsed)}
            cursor="pointer"
            width="full"
            display={{ base: 'none', md: 'flex' }}
          >
            <Icon as={MdMenu} boxSize={{ base: '2rem', md: '1.5rem' }} mr={2} />
            <Text
              display={{ base: 'none', md: isCollapsed ? 'none' : 'block' }}
              fontSize="lg"
            >
              {isCollapsed ? 'Expand' : 'Collapse'}
            </Text>
          </Flex>
        </VStack>
      </Box>
    </Flex>
  );

  return (
    <>
      {isMobile ? (
        <>
          <IconButton
            icon={<MdMenu />}
            onClick={onOpen}
            variant="ghost"
            aria-label="Open menu"
            position="fixed"
            top={4}
            left={4}
            zIndex={1}
          />
          <MobileNavigation />
        </>
      ) : (
        <DesktopNavigation />
      )}
    </>
  );
};

export default Navigation;
